import squarespaceIcon from 'assets/images/integrations/squarespace.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Squarespace — install the universal pixel via Code Injection (Header). Per-page firing
 * lives on the page-specific Code Block. Note: Code Injection is gated to Business plan
 * and above.
 */
export const squarespaceIntegration: PixelIntegration = {
  id: 'squarespace',
  label: 'Squarespace',
  description:
    'Install via Settings → Advanced → Code Injection (Header). Requires Business plan or higher.',
  icon: <img src={squarespaceIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://support.squarespace.com/hc/en-us/articles/205815908',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Confirm you're on Business plan or higher">
        <p className="text-base-xs text-primary-gray-500">
          Code Injection is unavailable on Personal plans. Upgrade to Business or Commerce if you
          need custom scripts.
        </p>
      </Step>
      <Step number={2} title="Open Settings → Advanced → Code Injection">
        <p className="text-base-xs text-primary-gray-500">
          From the Squarespace Home Menu: <strong>Settings → Advanced → Code Injection</strong>.
        </p>
      </Step>
      <Step number={3} title="Paste the universal pixel into the Header box">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          Paste into <strong>Header</strong> (not Footer) so the pixel loads early. Click{' '}
          <strong>Save</strong>.
        </p>
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>
      <Step number={4} title="Check for duplicate pixels">
        <p className="text-base-xs text-primary-gray-500">
          If you previously used Squarespace&apos;s native Meta Pixel integration (
          <strong>Marketing → Meta Pixel &amp; Ads</strong>), it won&apos;t be disabled
          automatically — both will fire side-by-side. Remove the native one if you only want one
          source of truth.
        </p>
      </Step>
      <Step number={5} title="(Optional) Fire from a specific page">
        <p className="text-base-xs text-primary-gray-500">
          For per-page conversions (thank-you, contact form), add the per-rule snippet below to that
          page&apos;s <strong>Page Settings → Advanced → Page Header Code Injection</strong>.
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildSquarespaceSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function buildSquarespaceSnippet(
  rule: ConversionRule,
  ctx: IntegrationContext,
): IntegrationSnippet {
  const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '\'<order total>\''
        : key === 'currency'
          ? '\'USD\''
          : key === 'order_number'
            ? '\'<order id>\''
            : '\'\'';

    lines.push(`  '${key}': ${placeholder}`);
  }
  const body = lines.join(',\n');

  const code = [
    '<script>',
    'if (window.CMPIXEL && CMPIXEL.fire) {',
    `  CMPIXEL.fire('${ctx.pixelCode}', {`,
    body,
    '  });',
    '}',
    '</script>',
  ].join('\n');

  return {
    code,
    language: 'html',
    label: 'Paste into Page Settings → Advanced → Page Header Code Injection',
  };
}
